import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { connect } from "react-redux";
import AutocompleteCompo from "../../components/Comman/AutoCompleteCompoForScheme";
import { blueFadeColor, redColor } from "../../config/ColorObj";
import { getStockStakeHolderType } from "../../Slice/stockStakeHolderType.slice";
import { showLoador } from "../Landing/Landing.slice";
import "./Scheme.css";
import { setSchemeFromDate } from "./schemestore";

/* ------------------ STYLED INPUT (UNCHANGED) ------------------ */
const CustomTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        height: 42,
        borderRadius: "10px",
        transition: "border-color 0.2s ease",
        "& fieldset": { borderColor: theme.palette.grey[300] },
        "&:hover fieldset": { borderColor: blueFadeColor },
        "&.Mui-focused fieldset": {
            borderColor: blueFadeColor,
            borderWidth: 1.5,
        },
    },
    "& .MuiOutlinedInput-input": {
        padding: "8px 10px",
        fontSize: "14px",
    },
    "& .MuiOutlinedInput-root.Mui-error fieldset": {
        borderColor: redColor,
    },
}));

const SchemeDetails = forwardRef((props, ref) => {
    const {
        getStockStakeHolderType,
        onChange,
        showLoador,
        stakeHolderTypeList,
    } = props;

    const [offerType, setOfferType] = useState(0);
    const offerTypeOptions = [
        { label: "Bill Wise", value: 0 },
        { label: "Credit Note", value: 1 },
    ];
    const [applicableOn, setApplicableOn] = useState(0);
    const applicableOnOptions = [
        { label: "Outlet", value: 0 },
        { label: "Stock Holder", value: 1 },
    ];
    const [calculatedBy, setCalculatedBy] = useState(0);
    const calculatedByOptions = [
        { label: "Secondary QTY", value: 0 },
        { label: "Primary QTY", value: 1 },
    ];
    const [offerLevel, setOfferLevel] = useState(0);
    const offerLevelOptions = [
        { label: "Bill Level", value: 0 },
        { label: "Item Level", value: 1 },
    ];
    const [form, setForm] = useState({
        offerName: "",
        description: "",
        fromDate: "",
        toDate: "",
        stakeholderType: null,
    });


    const [errors, setErrors] = useState({});
    const [shake, setShake] = useState({});


    useEffect(() => {
        const fetchData = async () => {
            try {
                showLoador({ loador: true });
                await getStockStakeHolderType({ userTypeIds: 2 });
            } finally {
                showLoador({ loador: false });
            }
        };
        fetchData();
    }, []);

    const stockHolderTypeOprions = useMemo(
        () =>
            Array.isArray(stakeHolderTypeList?.stockStakeHolderType)
                ? stakeHolderTypeList.stockStakeHolderType
                : [],
        [stakeHolderTypeList?.stockStakeHolderType]
    );


    const emitChange = (nextForm, nextExtras) => {
        onChange?.({
            ...nextForm,
            ...nextExtras,
        });
    };

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
        setShake((prev) => ({ ...prev, [key]: false }));
        const nextForm = { ...form, [key]: value };
        emitChange(nextForm, {
            offerType,
            applicableOn,
            calculatedBy,
            offerLevel,
        });
    };

    const handlePillChange = (key, value) => {
        const next = {
            offerType,
            applicableOn,
            calculatedBy,
            offerLevel,
            [key]: value,
        };

        if (key === "offerType") setOfferType(value);
        if (key === "applicableOn") setApplicableOn(value);
        if (key === "calculatedBy") setCalculatedBy(value);
        if (key === "offerLevel") setOfferLevel(value);

        emitChange(form, next);
    };

    /* ------------------ VALIDATION ------------------ */
    const validate = () => {
        let valid = true;
        const newErrors = {};
        const newShake = {};

        const rules = {
            offerName: !form.offerName,
            description: !form.description,
            fromDate: !form.fromDate,
            toDate: !form.toDate,
            stakeholderType:
                applicableOn == 1 && !form.stakeholderType,
        };

        Object.keys(rules).forEach((key) => {
            if (rules[key]) {
                valid = false;

                newErrors[key] = `${key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}     
        is required`;
                newShake[key] = true;

                setTimeout(() => {
                    setShake((prev) => ({ ...prev, [key]: false }));
                }, 400);
            }
        });

        setErrors(newErrors);
        setShake(newShake);

        return { isValid: valid, data: form };
    };

    /* ------------------ EXPOSE TO PARENT ------------------ */
    useImperativeHandle(ref, () => ({
        validate,
        getData: () => ({
            ...form,
            offerType,
            applicableOn,
            calculatedBy,
            offerLevel,
        }),
    }));

    return (
        <div className="card-scheme">
            <div className="card-title-scheme">📝 Offer Details</div>

            <div className="grid-scheme">
                <div className={shake.offerName ? "shake-error" : ""}>
                    <label className="required-label">Offer Name</label>
                    <input
                        className={`input-scheme ${errors.offerName ? "error-input" : ""}`}
                        placeholder="Offer Name"
                        value={form.offerName}
                        onChange={(e) => handleChange("offerName", e.target.value)}
                    />
                    {errors.offerName && (
                        <span className="error">{errors.offerName}</span>
                    )}
                </div>

                <div className={shake.description ? "shake-error" : ""}>
                    <label className="required-label">Offer Description</label>
                    <input
                        className={`input-scheme ${errors.description ? "error-input" : ""}`}
                        placeholder="Offer Description"
                        value={form.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                    />
                    {errors.description && (
                        <span className="error">{errors.description}</span>
                    )}
                </div>

                <div className={shake.fromDate ? "shake-error" : ""}>
                    <label className="required-label">From Date</label>
                    <input
                        className={`input-scheme ${errors.fromDate ? "error-input" : ""}`}
                        type="date"
                        value={form.fromDate}
                        onChange={(e) => {
                            handleChange("fromDate", e.target.value);
                            setSchemeFromDate(e.target.value); // shared update
                        }}
                    />
                    {errors.fromDate && <span className="error">{errors.fromDate}</span>}
                </div>

                <div className={shake.toDate ? "shake-error" : ""}>
                    <label className="required-label">To Date</label>
                    <input
                        className={`input-scheme ${errors.toDate ? "error-input" : ""}`}
                        type="date"
                        value={form.toDate}
                        onChange={(e) => handleChange("toDate", e.target.value)}
                    />
                    {errors.toDate && <span className="error">{errors.toDate}</span>}
                </div>

                <div>
                    <label>Offer Type</label>
                    <div className="pill-scheme-group">
                        {offerTypeOptions.map((type) => (
                            <div
                                key={type.value}
                                className={`pill-scheme  ${offerType === type.value ? "active" : ""}`}
                                onClick={() => handlePillChange("offerType", type.value)}
                            >
                                {type.label}
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <label>Applicable On</label>
                    <div className="pill-scheme-group">
                        {applicableOnOptions.map((val) => (
                            <div
                                key={val.value}
                                className={`pill-scheme  ${applicableOn === val.value ? "active" : ""}`}
                                onClick={() => handlePillChange("applicableOn", val.value)}
                            >
                                {val.label}
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <label>Offer Level</label>
                    <div className="pill-scheme-group">
                        {offerLevelOptions.map((val) => (
                            <div
                                key={val.value}
                                className={`pill-scheme  ${offerLevel === val.value ? "active" : ""}`}
                                onClick={() => handlePillChange("offerLevel", val.value)}
                            >
                                {val.label}
                            </div>
                        ))}
                    </div>
                </div>

                {offerType === 1 && (
                    <div>
                        <label>Calculated By</label>
                        <div className="pill-scheme-group">
                            {calculatedByOptions.map((type) => (
                                <div
                                    key={type.value}
                                    className={`pill-scheme  ${calculatedBy === type.value ? "active" : ""
                                        }`}
                                    onClick={() => handlePillChange("calculatedBy", type.value)}
                                >
                                    {type.label}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {applicableOn == 1 && (
                    <div className={shake.stakeholderType ? "shake-error" : ""}>
                        <label>Stake Holder Type</label>
                        <AutocompleteCompo
                            size="small"
                            options={stockHolderTypeOprions}
                            value={form.stakeholderType}
                            onChange={(e, val) => handleChange("stakeholderType", val)}
                            getOptionLabel={(o) => o.name}
                            renderInput={(params) => (
                                <CustomTextField
                                    {...params}
                                    placeholder="Select Stake Holder Type"
                                    error={Boolean(errors.stakeholderType)}
                                    helperText={errors.stakeholderType}
                                />
                            )}
                        />
                    </div>
                )}
            </div>
        </div>
    );
});


const mapStateToProps = (state) => ({
    stakeHolderTypeList: state.stockStakeHolderType,
});

const mapDispatchToProps = {
    getStockStakeHolderType,
    showLoador,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
})(SchemeDetails);